import { useState, useCallback } from 'react';
import { apiClient } from '../services/api.client';
import type { Client, UpdateClientDto } from '@lnr-v2/types';

interface UseUpdateClientReturn {
  updateClient: (id: string, data: UpdateClientDto) => Promise<Client>;
  loading: boolean;
  error: Error | null;
  reset: () => void;
}

export const useUpdateClient = (): UseUpdateClientReturn => {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<Error | null>(null);

  const updateClient = useCallback(
    async (id: string, data: UpdateClientDto): Promise<Client> => {
      setLoading(true);
      setError(null);

      try {
        const response = await apiClient.put(`clients/${id}`, {
          json: data,
        });

        const client = await response.json<Client>();
        return client;
      } catch (err) {
        let error: Error;

        if (err && typeof err === 'object' && 'response' in err) {
          try {
            const response = (err as { response: Response }).response;
            const errorData = await response.json();
            error = new Error(errorData.message || 'Failed to update client');
          } catch {
            error = new Error('Failed to update client');
          }
        } else if (err instanceof Error) {
          error = err;
        } else {
          error = new Error('Failed to update client');
        }

        setError(error);
        throw error;
      } finally {
        setLoading(false);
      }
    },
    []
  );

  const reset = useCallback(() => {
    setError(null);
  }, []);

  return {
    updateClient,
    loading,
    error,
    reset,
  };
};
